import { useState, useEffect, useRef, useCallback } from 'react';
import { TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InputText } from './InputText';
import { useDecimalSeparator } from '@/utils/formatting';
import {
    formatNumberForInput,
    parseLocaleNumber,
    hasWrongDecimalSeparator,
    hasInvalidThousandsGrouping
} from '@/utils/localeUtils';

export interface NumericInputProps extends Omit<
    TextFieldProps,
    'value' | 'onChange' | 'type'
> {
    /** Current numeric value. Pass `undefined` or `null` for empty. */
    value: number | null | undefined;
    /** Called with the parsed JS number (always dot-decimal) or null when empty/invalid. */
    onChange: (value: number | null) => void;
    /** Number of decimal digits to display. Defaults to 2. */
    digits?: number;
    /**
     * Override the detected decimal separator.
     * Detected automatically from company country when omitted.
     */
    decimalSeparator?: ',' | '.';
    inputAdornment?: React.ReactNode;
    spacing?: string;
    allowDisabledEdit?: boolean;
    onDisabledEditClick?: () => void;
    inputStyles?: React.CSSProperties;
    containerStyles?: React.CSSProperties;
    /** Called whenever the wrong-separator format error appears or clears. */
    onFormatError?: (hasError: boolean) => void;
    /**
     * Change this to discard typed text and any format error, e.g. on a form
     * Cancel. See `useNumericInputReset`.
     */
    resetKey?: number;
    testid?: string;
}

/**
 * Locale-aware numeric input component.
 *
 * - Danish / European companies: accepts and displays `100,00` (comma decimal)
 * - English / US companies: accepts and displays `100.00` (dot decimal)
 *
 * `onChange` always emits a native JS `number`, so JSON serialization to the
 * backend always uses dot decimal — backend invariant is preserved.
 *
 * Uses `type="text"` + `inputMode="decimal"` so:
 *  - The browser never hijacks the decimal key on mobile
 *  - Both comma and dot are accepted as characters
 */
export const NumericInput = ({
    testid: _testid,
    value,
    onChange,
    digits = 2,
    decimalSeparator: separatorProp,
    error: externalError,
    helperText: externalHelperText,
    onFormatError,
    resetKey,
    ...rest
}: NumericInputProps) => {
    const { t } = useTranslation();
    const { decimalSeparator: detectedSeparator } = useDecimalSeparator();
    const separator = separatorProp ?? detectedSeparator;

    /**
     * Convert the external numeric value to the display string.
     * Integers with digits=0 are shown without a decimal part.
     */
    const toDisplay = (num: number | null | undefined): string => {
        if (num === null || num === undefined) return '';
        return formatNumberForInput(num, separator, digits);
    };

    const [displayValue, setDisplayValue] = useState<string>(() =>
        toDisplay(value)
    );

    // Wrong separator detection for requirement: "show error when wrong format"
    const [hasSeparatorError, setHasSeparatorError] = useState(false);

    // Whole-number (digits === 0) input errors: a decimal, or a misplaced thousands separator
    const [wholeNumberError, setWholeNumberError] = useState<
        'decimal' | 'thousands' | null
    >(null);
    const hasDecimalError = wholeNumberError !== null;

    // Track whether the input is focused so we don't reformat mid-typing
    const isFocused = useRef(false);

    // Sync external value → display string (only when blurred). A value pushed
    // in while blurred (e.g. a form Cancel) replaces the typed text, so any
    // format error kept from blur no longer applies.
    useEffect(() => {
        if (!isFocused.current) {
            setDisplayValue(toDisplay(value));
            if (hasDecimalError || hasSeparatorError) {
                setWholeNumberError(null);
                setHasSeparatorError(false);
                onFormatError?.(false);
            }
        }
    }, [value, separator, digits]);

    // An input that unmounts with a kept error (closed dialog, hidden field)
    // must not leave its form blocked by a flag nothing can clear any more.
    const hasFormatErrorRef = useRef(false);
    const onFormatErrorRef = useRef(onFormatError);
    useEffect(() => {
        hasFormatErrorRef.current = hasDecimalError || hasSeparatorError;
        onFormatErrorRef.current = onFormatError;
    });
    useEffect(
        () => () => {
            if (hasFormatErrorRef.current) onFormatErrorRef.current?.(false);
        },
        []
    );

    // Invalid text is kept on blur, and a Cancel that restores the value the
    // parent already holds (invalid input is usually stored as 0/null) never
    // re-renders this input — so forms signal the reset explicitly.
    const lastResetKey = useRef(resetKey);
    useEffect(() => {
        if (lastResetKey.current === resetKey) return;
        lastResetKey.current = resetKey;
        setDisplayValue(toDisplay(value));
        setWholeNumberError(null);
        setHasSeparatorError(false);
        onFormatError?.(false);
    }, [resetKey]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setDisplayValue(raw);
        // In a whole-number field the thousands separator is valid ("1.000" in
        // Danish) only between proper 3-digit groups; the decimal separator never is.
        const wholeError =
            digits !== 0
                ? null
                : raw.includes(separator)
                  ? 'decimal'
                  : hasInvalidThousandsGrouping(raw, separator)
                    ? 'thousands'
                    : null;
        const noDecimalsError = wholeError !== null;
        const isWrong =
            digits !== 0 && hasWrongDecimalSeparator(raw, separator);
        setHasSeparatorError(isWrong);
        setWholeNumberError(wholeError);

        onFormatError?.(isWrong || noDecimalsError);

        // Allow typing a leading minus or trailing separator without emitting NaN
        if (
            raw === '' ||
            raw === '-' ||
            raw === separator ||
            raw === `-${separator}`
        ) {
            onChange(raw === '' ? null : null);
            return;
        }

        // Wrong separator or decimals not allowed: emit null so parents treat the value as invalid
        if (isWrong || noDecimalsError) {
            onChange(null);
            return;
        }

        const parsed = parseLocaleNumber(raw, separator);
        onChange(parsed);
    };

    const handleBlur = () => {
        isFocused.current = false;
        // Keep invalid text ("33,4" in an integer field, "10.5" in a comma
        // locale) and its error visible for the admin to fix: reformatting would
        // show "33" / "105" while the parent holds null, and clearing the error
        // would re-enable Save for a value nobody sees.
        if (hasDecimalError || hasSeparatorError) return;
        // Reformat the display value on blur (normalize trailing separator, extra zeros, etc.)
        const parsed = parseLocaleNumber(displayValue, separator);
        setDisplayValue(parsed !== null ? toDisplay(parsed) : '');
        setHasSeparatorError(false);
        setWholeNumberError(null);
        onFormatError?.(false);
    };

    const handleFocus = () => {
        isFocused.current = true;
    };

    const formatError =
        wholeNumberError === 'decimal'
            ? t('numeric_input.no_decimals_allowed')
            : wholeNumberError === 'thousands'
              ? t('numeric_input.invalid_thousands_separator', {
                    separator: separator === ',' ? '.' : ','
                })
              : hasSeparatorError
                ? separator === ','
                    ? t('numeric_input.use_comma_decimal')
                    : t('numeric_input.use_dot_decimal')
                : undefined;

    return (
        <InputText
            {...rest}
            type="text"
            inputProps={{
                inputMode: 'decimal',
                ...(rest.inputProps ?? {})
            }}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            error={externalError || hasSeparatorError || hasDecimalError}
            helperText={formatError ?? externalHelperText}
        />
    );
};

/**
 * Reset signal for the `NumericInput`s in a form: pass `resetKey` to each
 * input and call `resetInputs` from the form's Cancel.
 */
export const useNumericInputReset = () => {
    const [resetKey, setResetKey] = useState(0);
    const resetInputs = useCallback(() => setResetKey((key) => key + 1), []);
    return { resetKey, resetInputs };
};
